import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Heading, Text, Chart, Pagination, Badge, Button, ChipGroup, DatePicker, Icon } from '../../../components/base';
import {
  AipLogDetail,
  AipLogItem,
  AipSummary,
  AipTrend,
  PeriodKey,
  getMaxSelectableDate,
  isCustomRangeExceeded,
  periodOptions,
  resolvePeriodRange,
  uploadTypeLabel,
} from '../../../libs/models/dashboard/aip/AipMonitoring';
import { DashboardService } from '../../../libs/services/DashboardService';
import { useAppDispatch } from '../../../redux/app/hooks';
import { showAlertToast } from '../../../redux/features/app/appSlice';
import { AlertType } from '../../../libs/models/AlertType';
import AipDetailDialog from './AipDetailDialog';
import AipPieChart from './AipPieChart';

const PAGE_SIZE = 20;

const formatDateTime = (iso: string) => iso.replace('T', ' ').slice(0, 19);

const emptySummary: AipSummary = {
  totalCount: 0,
  decryptSuccessCount: 0,
  decryptFailCount: 0,
  privacyCount: 0,
  bySource: [],
};

const AipMonitoring = () => {
  const dispatch = useAppDispatch();
  const [period, setPeriod] = useState<PeriodKey>('today');
  const [customRange, setCustomRange] = useState<[string, string] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const [summary, setSummary] = useState<AipSummary>(emptySummary);
  const [trend, setTrend] = useState<AipTrend>({ unit: 'Day', points: [] });
  const [logs, setLogs] = useState<AipLogItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const statsRefreshedKeyRef = useRef(0);
  const logsRefreshedKeyRef = useRef(0);

  const [detail, setDetail] = useState<AipLogDetail | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  const service = useMemo(() => new DashboardService(), []);
  const range = useMemo(() => resolvePeriodRange(period, customRange), [period, customRange]);
  const maxSelectableDate = useMemo(() => getMaxSelectableDate(), []);

  const canQuery = period !== 'custom' || (!!customRange?.[0] && !!customRange?.[1]);

  // 기간이 바뀌면 첫 페이지부터 다시 본다.
  useEffect(() => {
    setPage(1);
  }, [range.from, range.to]);

  useEffect(() => {
    if (!canQuery) {
      setSummary(emptySummary);
      setTrend({ unit: 'Day', points: [] });
      setError(null);
      return;
    }
    let isActive = true;

    const loadStats = async () => {
      setError(null);
      setSummary(emptySummary);
      setTrend({ unit: 'Day', points: [] });

      const refresh = refreshKey > 0 && statsRefreshedKeyRef.current !== refreshKey;
      statsRefreshedKeyRef.current = refreshKey;

      try {
        const [summaryResult, trendResult] = await Promise.all([
          service.getAipSummaryAsync(range.from, range.to, undefined, refresh),
          service.getAipTrendAsync(range.from, range.to, undefined, refresh),
        ]);

        if (!isActive) return;

        setSummary({ ...emptySummary, ...summaryResult, bySource: summaryResult?.bySource ?? [] });
        setTrend({
          unit: trendResult?.unit ?? 'Day',
          points: trendResult?.points ?? [],
        });
      } catch (e) {
        if (!isActive) return;

        setError(e instanceof Error ? e.message : '데이터를 불러오지 못했습니다.');
        setSummary(emptySummary);
        setTrend({ unit: 'Day', points: [] });
      }
    };

    void loadStats();

    return () => {
      isActive = false;
    };
  }, [service, range.from, range.to, canQuery, refreshKey]);

  useEffect(() => {
    if (!canQuery) {
      setLogs([]);
      setTotalCount(0);
      return;
    }
    let isActive = true;

    const loadLogs = async () => {
      setIsLoading(true);
      setLogs([]);
      setTotalCount(0);

      const refresh = refreshKey > 0 && logsRefreshedKeyRef.current !== refreshKey;
      logsRefreshedKeyRef.current = refreshKey;

      try {
        const logsResult = await service.getAipLogsAsync(range.from, range.to, page, pageSize, undefined, refresh);

        if (!isActive) return;

        setLogs(logsResult.data ?? []);
        setTotalCount(logsResult.totalCount ?? 0);
      } catch (e) {
        if (!isActive) return;

        setError(e instanceof Error ? e.message : '목록을 불러오지 못했습니다.');
        setLogs([]);
        setTotalCount(0);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    void loadLogs();

    return () => {
      isActive = false;
    };
  }, [service, range.from, range.to, page, pageSize, canQuery, refreshKey]);

  // 상세보기 클릭
  const openDetail = useCallback(
    async (item: AipLogItem) => {
      setIsDetailLoading(true);
      try {
        const result = await service.getAipLogDetailAsync(item.userId, item.id);
        setDetail(result);
      } catch {
        setError('상세 정보를 불러오지 못했습니다.');
      } finally {
        setIsDetailLoading(false);
      }
    },
    [service],
  );

  // KPI 카드
  const kpis = [
    { label: '업로드된 문서 건수', value: summary.totalCount },
    {
      label: 'AIP 복호화 문서 건수 / 실패 건수',
      value: `${summary.decryptSuccessCount} / ${summary.decryptFailCount}`,
    },
    { label: '개인정보 문서 건수', value: summary.privacyCount },
  ];

  const isHourly = trend.unit === 'Hour';
  const trendTitle = isHourly ? '시간대별 업로드 건수 추이' : '일별 업로드 건수 추이';

  const trendPoints = trend.points ?? [];

  const loadedAtText = summary.loadedAt ? summary.loadedAt.slice(11, 16) : '';
  const bySource = summary.bySource ?? [];

  const lineData = {
    labels: trendPoints.map((t) => (isHourly ? t.date : t.date.slice(5).replace('-', '/'))),
    datasets: [
      { label: '전체', data: trendPoints.map((t) => t.total), fill: false },
      { label: '성공', data: trendPoints.map((t) => t.success), fill: false },
      { label: '실패', data: trendPoints.map((t) => t.fail), fill: false },
    ],
  };

  return (
    <div className="aip-dashboard">
      <div className="aip-head">
        <div className="aip-head-title">
          <Heading as="h1" size="title3" weight="semibold">
            사내문서 업로드 모니터링 대시보드
          </Heading>
          {loadedAtText && (
            <button
              type="button"
              className="aip-loaded-at"
              disabled={isLoading || !canQuery}
              onClick={() => {
                setRefreshKey((k) => k + 1);
              }}
              title="새로고침"
            >
              <Icon name="refresh" className="aip-loaded-at-icon" />
              <span className="aip-loaded-at-text">{loadedAtText} 업데이트</span>
            </button>
          )}
        </div>
        <div className="aip-period">
          {/* 기간 선택: 공통 ChipGroup (오늘/이번주/이번달/기간) */}
          <ChipGroup
            variant="filled"
            options={periodOptions}
            value={period}
            onChange={(v) => {
              setPeriod(v as PeriodKey);
            }}
          />
          {period === 'custom' && (
            <DatePicker
              dateRange
              maxDate={maxSelectableDate}
              value={customRange ?? undefined}
              onChange={(v) => {
                const next = Array.isArray(v) ? (v as [string, string]) : null;
                if (isCustomRangeExceeded(next)) {
                  dispatch(
                    showAlertToast({
                      altMessage: '조회 기간은 최대 1개월까지 선택할 수 있어요.',
                      altType: AlertType.Warning,
                    }),
                  );
                  return;
                }
                setCustomRange(next);
              }}
            />
          )}
        </div>
      </div>

      {error && (
        <div className="aip-error">
          <Text size="body2">{error}</Text>
        </div>
      )}

      {/* KPI 카드 */}
      <div className="aip-kpi-grid">
        {kpis.map((kpi, i) => (
          <div key={i} className="aip-kpi-card">
            <Text size="body2" className="aip-kpi-label">
              {kpi.label}
            </Text>
            <strong className="aip-kpi-value">{isLoading ? '-' : kpi.value}</strong>
          </div>
        ))}
      </div>

      {/* 차트 영역 */}
      <div className="aip-chart-row">
        <div className="aip-chart-box aip-chart-line">
          <Heading as="h2" size="body2" weight="semibold" className="aip-box-title">
            {trendTitle}
          </Heading>
          <div className="aip-chart-canvas">
            <Chart
              chartLabel={trendTitle}
              chartType="line"
              chartData={lineData}
              chartOptions={{
                interaction: { mode: 'index', intersect: false },
                plugins: {
                  tooltip: { mode: 'index', intersect: false },
                  legend: { position: 'top', align: 'end' },
                },
                scales: { y: { ticks: { precision: 0 } } },
              }}
            />
          </div>
        </div>
        <div className="aip-chart-box aip-chart-pie">
          <Heading as="h2" size="body2" weight="semibold" className="aip-box-title">
            출처별 업로드 건수
          </Heading>
          <div className="aip-chart-canvas">
            {/* 공통 Chart는 pie 조각 색 구분/축 제거 불가 → 대시보드 전용 pie 사용 */}
            <AipPieChart
              label="출처별 업로드 건수"
              labels={bySource.map((s) => uploadTypeLabel[s.uploadType])}
              data={bySource.map((s) => s.count)}
            />
          </div>
        </div>
      </div>

      {/* 업로드 상세 목록 */}
      <div className="aip-list-box">
        <div className="aip-list-head">
          <Heading as="h2" size="body2" weight="semibold">
            업로드 상세
          </Heading>
        </div>

        {/* 공통 테이블 스타일(dsx-Table) 사용 */}
        <div className="dsx-TableWrapper aip-table-wrap">
          <table className="dsx-Table">
            <thead className="dsx-Table-header">
              <tr className="dsx-Table-row">
                <th className="dsx-Table-column">일시</th>
                <th className="dsx-Table-column">이름 / 소속</th>
                <th className="dsx-Table-column">라벨명</th>
                <th className="dsx-Table-column">파일명</th>
                <th className="dsx-Table-column">성공/실패</th>
                <th className="dsx-Table-column">상세보기</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr className="dsx-Table-row">
                  <td className="dsx-Table-cell aip-empty-cell" colSpan={6}>
                    불러오는 중...
                  </td>
                </tr>
              )}
              {!isLoading && logs.length === 0 && (
                <tr className="dsx-Table-row">
                  <td className="dsx-Table-cell aip-empty-cell" colSpan={6}>
                    {canQuery ? '조회된 데이터가 없습니다.' : '조회할 기간을 선택해 주세요.'}
                  </td>
                </tr>
              )}
              {!isLoading &&
                logs.map((item) => (
                  <tr key={item.id} className="dsx-Table-row">
                    <td className="dsx-Table-cell">{formatDateTime(item.uploadedAt)}</td>
                    <td className="dsx-Table-cell">
                      <span className="aip-user">{item.userName}</span>
                      {item.deptName && <span className="aip-dept"> / {item.deptName}</span>}
                    </td>
                    <td className="dsx-Table-cell aip-label">{item.labelName}</td>
                    <td className="dsx-Table-cell">{item.fileName}</td>
                    <td className="dsx-Table-cell">
                      {/* 성공/실패 상태 뱃지 (공통 Badge) */}
                      <Badge variant="tint" size="large" accent={item.isSuccess ? 'positive' : 'negative'}>
                        {item.isSuccess ? '성공' : '실패'}
                      </Badge>
                    </td>
                    <td className="dsx-Table-cell">
                      {/* 건건이 상세보기 (공통 Button) */}
                      <Button
                        variant="text"
                        size="small"
                        suffixIcon="chevron"
                        disabled={isDetailLoading}
                        onClick={() => {
                          void openDetail(item);
                        }}
                      >
                        상세보기
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="aip-pagination">
          <Pagination
            totalResults={totalCount}
            page={page}
            setPage={setPage}
            resultsPerPage={pageSize}
            setPageSize={setPageSize}
            options={['total', 'sizes']}
            pageSizes={[20, 50, 100]}
          />
        </div>
      </div>

      <AipDetailDialog
        detail={detail}
        onClose={() => {
          setDetail(null);
        }}
      />
    </div>
  );
};

export default AipMonitoring;
